import type { EventDto, EventRegistrationWindowStatus, EventScheduleStatus, EventStatus } from "@/entities/event/types"
import { parseEventApiInstant } from "@/entities/event/parseEventApiInstant"

const eventInstantMs = (value: string) => parseEventApiInstant(value).getTime()

export const getEventScheduleStatus = (
  startTime: string,
  endTime: string,
  now: Date = new Date(),
): EventScheduleStatus => {
  const start = eventInstantMs(startTime)
  const end = eventInstantMs(endTime)
  const t = now.getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return -1
  if (t < start) return 0
  if (t < end) return 1
  return 2
}

export const getEventRegistrationWindowStatus = (
  registrationStartTime: string | null | undefined,
  registrationEndTime: string | null | undefined,
  now: Date = new Date(),
): EventRegistrationWindowStatus => {
  if (!registrationStartTime || !registrationEndTime) return -1
  const start = eventInstantMs(registrationStartTime)
  const end = eventInstantMs(registrationEndTime)
  const t = now.getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return -1
  if (t < start) return 0
  if (t < end) return 1
  return 2
}

type EventTransitionGuardInput = Pick<
  EventDto,
  "status" | "startTime" | "endTime" | "registrationStartTime" | "registrationEndTime"
>

export const canCancelEvent = (event: EventTransitionGuardInput, now: Date = new Date()) => {
  if (event.status !== 2) return false
  const end = eventInstantMs(event.endTime)
  if (Number.isNaN(end)) return false
  return end > now.getTime()
}

export const canDeleteEvent = (event: EventTransitionGuardInput, now: Date = new Date()) => {
  void now
  if (event.status === 4) return true
  if (event.status === 3) return true
  return event.status === 0
}

/** Редактирование из портала «Мои мероприятия»: черновик, отклонённое или отменённое мероприятие. */
export const canEditManagedEventInPortal = (event: Pick<EventDto, "status">) => {
  const s = event.status as EventStatus
  return s === 0 || s === 3 || s === 4
}

/** Отправка на модерацию из превью портала без открытия формы — только черновик. */
export const canSubmitDraftForModerationInPortal = (event: Pick<EventDto, "status">) => {
  const s = event.status as EventStatus
  return s === 0
}

/** Отзыв с модерации организатором — пока на рассмотрении. */
export const canWithdrawEventFromModeration = (event: Pick<EventDto, "status">) => Number(event.status) === 1

export const isCurrentUserEventInitiator = (
  event: Pick<EventDto, "creatorId">,
  currentUserId: string | null | undefined,
): boolean => {
  if (!currentUserId || String(currentUserId).trim() === "") return false
  const cid = event.creatorId
  if (cid === undefined || cid === null || String(cid).trim() === "") return false
  return String(cid).trim() === String(currentUserId).trim()
}

/** Отзыв с модерации в UI — только у инициатора и только в статусе «на модерации». */
export const canWithdrawEventFromModerationAsInitiator = (
  event: Pick<EventDto, "status" | "creatorId">,
  currentUserId: string | null | undefined,
) => canWithdrawEventFromModeration(event) && isCurrentUserEventInitiator(event, currentUserId)
